// The states the app can be in, as one value.
//
// There are exactly five, and every one of them has a screen:
//   demo        — no configuration at all. The fixed dataset, the "Demo data" badge. Zero setup.
//   loading     — configured, and we are asking the browser's stored session whether it is still
//                 valid. Usually milliseconds, but rendering the login form during it would flash a
//                 form at someone who is already signed in.
//   signed-out  — configured, no session. The login screen.
//   signed-in   — configured, session in hand. The real app, reading through the Worker.
//   unreachable — configured, and the check itself did not finish: it threw, or it was still
//                 running after SESSION_CHECK_TIMEOUT_MS. This is NOT signed-out. When the stored
//                 session has expired, getSession() refreshes it over the network, and on a VPN or
//                 a blocked network that fetch can hang forever (and supabase-js's Web Locks use on
//                 WebKit can deadlock it too). The app never fakes knowledge, so it says it could
//                 not confirm the session rather than claiming the owner is signed out.
//
// Nothing else in the app is allowed to ask "is there a session?" — it asks for this value and
// renders the branch, so a sixth half-state cannot appear somewhere by accident.

import { useCallback, useEffect, useState } from 'react'

import { getSupabase, remoteConfig, type Session } from './supabase'

/** How long the session check may run before the gate stops waiting and says so. */
export const SESSION_CHECK_TIMEOUT_MS = 8_000

/** The sentence the gate shows when the check simply never came back. */
export const SESSION_TIMEOUT_REASON = `Could not reach the sign-in service after ${
  SESSION_CHECK_TIMEOUT_MS / 1000
} seconds.`

export type SessionState =
  | { status: 'demo' }
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session }
  | { status: 'unreachable'; reason: string }

export interface SessionCheck {
  state: SessionState
  /** Runs the session check again from the top (back to 'loading', a fresh timeout). */
  retry: () => void
  /**
   * Gives up on the stored session: shows the login form now and clears the stored session in the
   * background. Not awaited — the clear can go through the same stuck network/lock that caused the
   * hang, and the owner must not be left waiting on it.
   */
  signInAgain: () => void
}

/** A thrown value, as a sentence worth showing. Never empty. */
function reasonFrom(thrown: unknown): string {
  const message = thrown instanceof Error ? thrown.message : typeof thrown === 'string' ? thrown : ''
  return message.trim() || 'The sign-in check failed without saying why.'
}

/** The full check, with the controls the gate needs. Everything else wants `useSession()`. */
export function useSessionCheck(): SessionCheck {
  const configured = remoteConfig() !== null
  const [state, setState] = useState<SessionState>(configured ? { status: 'loading' } : { status: 'demo' })
  // Bumped by retry(): re-runs the effect below, which resubscribes and re-checks.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const supabase = getSupabase()
    if (!supabase) return

    let cancelled = false
    let settled = false

    const timer = setTimeout(() => {
      if (cancelled || settled) return
      setState({ status: 'unreachable', reason: SESSION_TIMEOUT_REASON })
    }, SESSION_CHECK_TIMEOUT_MS)

    // Started inside a .then so a synchronous throw lands in the same .catch as a rejection.
    void Promise.resolve()
      .then(() => supabase.auth.getSession())
      .then(({ data }) => {
        settled = true
        clearTimeout(timer)
        if (cancelled) return
        // A late answer (after the timeout already fired) is still a real answer, so it wins.
        setState(data.session ? { status: 'signed-in', session: data.session } : { status: 'signed-out' })
      })
      .catch((thrown: unknown) => {
        settled = true
        clearTimeout(timer)
        if (cancelled) return
        setState({ status: 'unreachable', reason: reasonFrom(thrown) })
      })

    // Covers sign-in, sign-out, and the token refresh that happens every hour on a tab left open —
    // and a session that finally arrives after the check above gave up waiting.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return
      setState(session ? { status: 'signed-in', session } : { status: 'signed-out' })
    })

    return () => {
      cancelled = true
      clearTimeout(timer)
      subscription.subscription.unsubscribe()
    }
  }, [attempt])

  const retry = useCallback(() => {
    if (!getSupabase()) return
    setState({ status: 'loading' })
    setAttempt((n) => n + 1)
  }, [])

  const signInAgain = useCallback(() => {
    const supabase = getSupabase()
    if (!supabase) return
    setState({ status: 'signed-out' })
    void Promise.resolve()
      .then(() => supabase.auth.signOut({ scope: 'local' }))
      .catch(() => {
        // Nothing to tell the owner: the login form is already up, and a fresh sign-in replaces
        // whatever is stored.
      })
  }, [])

  return { state, retry, signInAgain }
}

export function useSession(): SessionState {
  return useSessionCheck().state
}

/** Ends the session. The auth listener above turns this into the login screen; nothing else to do. */
export async function signOut(): Promise<void> {
  await getSupabase()?.auth.signOut()
}
